import React, { ReactElement, useContext, useState } from 'react';
import { isEmpty } from 'lodash';
import { LoadableButton } from '@mo/loadable-button';
import { PageErrorBox } from '@mo/page-error-box';
import { Link, useHistory } from 'react-router-dom';
import styles from '../loginPage.module.scss';
import { Title } from '../../../components/title/Title';
import PasswordInput from './passwordInput/PasswordInput';
import { checkUsernameAndPassword } from '../usePingAuthFlow/pingAuthFlowGateway';
import { AuthLink } from '../../../domain/AuthFlowState';
import { TechnicalError } from '../../../components/technicalError/TechnicalError';
import {
    AccountLockedError,
    AuthFailureError,
    CredentialsMismatchError,
    ServiceUnavailableError,
    SessionExpiredError,
} from '../usePingAuthFlow/errors';
import ClientErrorBox from './ClientErrorBox';
import { mapPageValidationErrors, validate } from './validation/loginValidation';
import SimpleInput from '../../../components/input/SimpleInput';
import { FormattedString, FormattingKeys } from '../../../components/formattedString/FormattedString';
import { BASE_PATH, EXPIRED_PASSWORD_PATH, FORGOT_PASSWORD_PAGE } from '../../../common/routes';
import { appContext } from '../../../components/appContext/AppContext';

export interface LoginFormProps {
    flowId: string;
    loginUrl: AuthLink;
}

const ForgottenPasswordLink = ({ flowId, text }: { flowId: string; text: string }): ReactElement => {
    return (
        <Link to={{ pathname: FORGOT_PASSWORD_PAGE, search: `?flowId=${flowId}`, state: { displayBack: true } }}>
            {text}
        </Link>
    );
};

export function LoginForm(props: LoginFormProps): ReactElement {
    const { window } = useContext(appContext);
    const history = useHistory();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string[] }>({});
    const [serviceError, setServiceError] = useState<undefined | ServiceUnavailableError | AuthFailureError>(undefined);

    const onKeyPress = async (event: React.KeyboardEvent<HTMLInputElement>): Promise<void> => {
        if (event.key === 'Enter') {
            return validateAndSubmit();
        }
    };

    const validateAndSubmit = async (): Promise<void> => {
        try {
            setServiceError(undefined);
            setValidationErrors({});
            const trimmedUsername = username.trim();
            await validate({ username: trimmedUsername, password: password });
            await submitUsernameAndPassword(trimmedUsername, password);
        } catch (errors: any) {
            setValidationErrors(errors);
        }
    };

    const submitUsernameAndPassword = async (username: string, password: string): Promise<void> => {
        try {
            setLoading(true);
            const { status, location } = await checkUsernameAndPassword(
                { username: username, password, url: props.loginUrl.href },
                props.flowId
            );
            if (status === 'MUST_CHANGE_PASSWORD') {
                history.push(`${EXPIRED_PASSWORD_PATH}${username}`);
            }
            if (status === 'RESUME' && location) {              
                window.location.assign(location + window.location.hash);
            }
        } catch (error: any) {
            if (error instanceof AuthFailureError) setPassword('');
            setServiceError(error);
            setLoading(false);
        }
    };
    const failedAuth = serviceError instanceof AuthFailureError;  

    return (
        <>
            <div className={styles.form}>
                <PageErrorBox
                    hasErrors={!isEmpty(validationErrors)}
                    errors={mapPageValidationErrors(validationErrors)}
                />

                {serviceError instanceof CredentialsMismatchError && (
                    <ClientErrorBox>
                        <FormattedString
                            string={serviceError.message}
                            constant={FormattingKeys.FORGOTTEN_PASSWORD_LINK}
                            element={<ForgottenPasswordLink flowId={props.flowId} text={'reset it here'} />}
                        />
                    </ClientErrorBox>
                )}
                {serviceError instanceof AccountLockedError && (
                    <ClientErrorBox>
                        Your account has been locked as there have been too many failed attempts to sign in. Please{' '}
                        <Link to={FORGOT_PASSWORD_PAGE}>reset your password</Link> to unlock your account. If you continue 
                        to have issues after resetting your password please contact your team at Manufacturer Relations{' '} 
                        <a href='mailto:manufacturerrelations@motabilityoperations.co.uk'>manufacturerrelations@motabilityoperations.co.uk </a> 
                        or DFW and adaptations <a href='mailto:dfwandadaptation@motabilityoperations.co.uk'>dfwandadaptation@motabilityoperations.co.uk</a>.
                    </ClientErrorBox>
                )}

                {serviceError instanceof SessionExpiredError && (
                    <ClientErrorBox>
                        Your session has expired due to inactivity. Please <Link to={BASE_PATH}>refresh this page</Link>{' '}
                        to sign in.
                    </ClientErrorBox>
                )}

                <Title title='Sign in' className={styles.header} />

                <SimpleInput
                    inputFieldClassName={styles.inputField}
                    groupClassName={styles.inputGroup}
                    labelText='Username'
                    inputName='username'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        setUsername(e.target.value);
                    }}
                    value={username}
                    onKeyPress={onKeyPress}
                    autoCapitalize={'none'}
                    highlightError={failedAuth}
                    errors={validationErrors && validationErrors.username}
                    disabled={serviceError && serviceError instanceof SessionExpiredError}
                />

                <PasswordInput
                    onChange={(newPassword: string): void => setPassword(newPassword)}
                    password={password}
                    handleSubmit={validateAndSubmit}
                    highlightError={failedAuth}
                    errors={validationErrors && validationErrors.password}
                    disabled={serviceError && serviceError instanceof SessionExpiredError}
                />

                <ForgottenPasswordLink flowId={props.flowId} text={'Forgotten password?'} />

                {serviceError instanceof ServiceUnavailableError && (
                    <TechnicalError>
                        Sorry, we're having some technical difficulties. Please try again 
                        in a few moments. If the problem persist please contact your team 
                        at Manufacturer Relations <a href='mailto:manufacturerrelations@motabilityoperations.co.uk'>manufacturerrelations@motabilityoperations.co.uk </a> 
                        or DFW and adaptations <a href='mailto:dfwandadaptation@motabilityoperations.co.uk'>dfwandadaptation@motabilityoperations.co.uk</a>.
                    </TechnicalError>
                )}

                <div className={styles.buttonGroup}>
                    <LoadableButton
                        loading={loading}
                        loadingText='Signing in'
                        notLoadingText='Sign in'
                        className={styles.signInButton}
                        data-ga='sign-in-button'
                        onClick={validateAndSubmit}
                        disabled={serviceError && serviceError instanceof SessionExpiredError}
                    />
                </div>
            </div>
        </>
    );
}
